import { useEffect, useRef, useState } from "react";
import { ImagePlus, Trash2, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, galleryImageUrl } from "@/lib/api";

const CATEGORIES = ["Bridal Makeup", "Party Makeup", "Hairstyle", "Facial", "Nail Care", "Saree Draping", "Other"];

export function GalleryManager() {
  const [photos, setPhotos] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { api.get("/gallery").then(({ data }) => setPhotos(data)).catch(() => setPhotos([])); }, []);
  useEffect(() => {
    if (!file) { setPreview(""); return undefined; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const pick = (f) => {
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) { toast.error("Please choose a JPG, PNG or WEBP image"); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error("Image must be under 8 MB"); return; }
    setFile(f);
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a photo first"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title || category);
    fd.append("category", category);
    try {
      const { data } = await api.post("/admin/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos((list) => [data, ...(list || [])]);
      setFile(null); setTitle("");
      if (inputRef.current) inputRef.current.value = "";
      toast.success("Photo added to your gallery");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this photo from the gallery?")) return;
    try {
      await api.delete(`/admin/gallery/${id}`);
      setPhotos((list) => list.filter((p) => p.id !== id));
      toast.success("Photo removed");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <section data-testid="gallery-manager">
      <header className="admin-head"><div><p className="eyebrow light">YOUR WORK, ON DISPLAY</p><h1>Gallery</h1></div><p className="muted">Photos you add here replace the sample images on the website.</p></header>
      <form className="upload-card" onSubmit={upload} data-testid="gallery-upload-form">
        <label className={`dropzone ${preview ? "has-preview" : ""}`} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files[0]); }} data-testid="gallery-dropzone">
          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => pick(e.target.files[0])} data-testid="gallery-file-input" />
          {preview ? <img src={preview} alt="Preview" data-testid="gallery-upload-preview" /> : <><ImagePlus size={28} /><span>Drop a photo here or click to choose</span><small>JPG, PNG or WEBP · up to 8 MB</small></>}
        </label>
        <div className="upload-fields">
          <label>Title<input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Reception glam for Priya" data-testid="gallery-title-input" /></label>
          <label>Category<select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="gallery-category-select">{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></label>
          <button className="gold-button" type="submit" disabled={busy || !file} data-testid="gallery-upload-submit"><UploadCloud size={16} /> {busy ? "Uploading…" : "Add to gallery"}</button>
        </div>
      </form>
      {photos === null ? <p className="muted">Loading photos…</p> : photos.length === 0 ? <p className="muted empty" data-testid="gallery-empty">No photos yet — the website is showing sample images until you add your own.</p> : (
        <div className="photo-grid" data-testid="admin-photo-grid">{photos.map((p) => <figure key={p.id} data-testid={`admin-photo-${p.id}`}><img src={galleryImageUrl(p.id)} alt={p.title} loading="lazy" /><figcaption><span>{p.category}</span><strong>{p.title}</strong></figcaption><button onClick={() => remove(p.id)} aria-label="Remove photo" data-testid="gallery-delete-button"><Trash2 size={15} /></button></figure>)}</div>
      )}
    </section>
  );
}
